import { useState, type ReactNode } from "react";
import { Button, Pill } from "@/components/ui";
import { TextField, TextAreaField } from "@/components/ui";
import { FormDrawer } from "@/staff/jobs/FormDrawer";
import { formatDateShort, formatRelative } from "@/lib/format";
import type { FeefoRequestStatus } from "@/types";
// Reuse the feefo__ styles first authored for the job-detail Feedback tab.
import "@/staff/jobs/FeedbackTab.css";

/** The minimal row shape shared by the job-detail tab and the cross-job feeds —
 *  satisfied by FeefoRequest, FeefoOpenItem and FeefoReceivedItem alike. Review
 *  fields are optional so open (un-responded) items fit without them. */
export interface FeefoRowData {
  order_reference: string;
  milestone: string;
  sent_at: number;
  stage_note: string;
  status: FeefoRequestStatus;
  response_received_at: number;
  review_rating?: number;
  review_title?: string;
  review_text?: string;
  review_url?: string;
  last_checked_at?: number;
  client_name?: string;
  client_email?: string;
}

/** Five-star rating from a 1–5 value (rounded). Decorative + labelled. */
export function Stars({ rating }: { rating: number }) {
  const filled = Math.max(0, Math.min(5, Math.round(rating)));
  return (
    <span className="feefo-stars" aria-label={`${rating} out of 5`} title={`${rating} / 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} className={`feefo-star${n <= filled ? " feefo-star--on" : ""}`} aria-hidden="true">
          ★
        </span>
      ))}
    </span>
  );
}

export function statusPill(status: FeefoRequestStatus) {
  if (status === "responded") return <Pill tone="success">Responded</Pill>;
  if (status === "failed") return <Pill tone="danger">Send failed</Pill>;
  return <Pill tone="info-soft">Awaiting response</Pill>;
}

/** One request row — order ref + milestone + sent date + status/stars, and the
 *  review body once responded (self-manages the "show more" expand). `header`
 *  renders above the row (the cross-job feeds pass a link to the job). */
export function FeefoRequestRow({
  r,
  header,
  hideStatus = false,
}: {
  r: FeefoRowData;
  header?: ReactNode;
  /** Hide the status pill (e.g. the Feedback-received feed, where every row is
   *  "Responded" — the stars already convey it). */
  hideStatus?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const responded = r.status === "responded";
  const reviewText = r.review_text ?? "";
  const longText = reviewText.length > 180;
  return (
    <div className="feefo__row">
      {header ? <div className="feefo__row-job">{header}</div> : null}
      <div className="feefo__row-top">
        <div className="feefo__row-meta">
          <span className="feefo__ref">{r.order_reference}</span>
          {r.milestone && r.milestone !== "manual" ? <span>· {r.milestone.replace(/_/g, " ")}</span> : null}
          <span>· sent {formatDateShort(r.sent_at)}</span>
          {r.stage_note ? <span title="Stage / reason note">· {r.stage_note}</span> : null}
        </div>
        <div className="feefo__row-status">
          {responded ? <Stars rating={r.review_rating ?? 0} /> : null}
          {hideStatus ? null : statusPill(r.status)}
        </div>
      </div>

      {responded ? (
        <div className="feefo__review">
          {r.review_title ? <div className="feefo__review-title">{r.review_title}</div> : null}
          {reviewText ? (
            <p className={`feefo__review-text${longText && !expanded ? " feefo__review-text--clamped" : ""}`}>
              {reviewText}
            </p>
          ) : null}
          <div className="feefo__review-foot">
            {longText ? (
              <button type="button" className="feefo__expand" onClick={() => setExpanded((e) => !e)}>
                {expanded ? "Show less" : "Show more"}
              </button>
            ) : null}
            {r.review_url ? (
              <a className="feefo__link" href={r.review_url} target="_blank" rel="noopener noreferrer">
                View on Feefo ↗
              </a>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="feefo__row-meta feefo__row-meta--sub">
          {(r.last_checked_at ?? 0) > 0 ? (
            <span>Last checked {formatRelative(r.last_checked_at as number)}</span>
          ) : (
            <span>Not yet checked</span>
          )}
          {r.client_name || r.client_email ? <span>· asked as {r.client_name || r.client_email}</span> : null}
        </div>
      )}
    </div>
  );
}

/** Confirm-before-send drawer. Previews the resolved client + the order ref that
 *  will be generated (also the guard against emailing the wrong person), and
 *  soft-warns on a re-ask within ~7 days. */
export function NewRequestDrawer({
  clientName,
  clientEmail,
  predictedOrderRef,
  recentlyAsked,
  lastAskedLabel,
  busy,
  cancelLabel = "Cancel",
  onCancel,
  onConfirm,
}: {
  clientName: string;
  clientEmail: string;
  predictedOrderRef: string;
  recentlyAsked: boolean;
  lastAskedLabel: string;
  busy: boolean;
  /** Secondary button text — "Cancel" (closes) on the job tab, "Back" (returns to
   *  the job picker) in the standalone two-step flow. */
  cancelLabel?: string;
  onCancel: () => void;
  onConfirm: (description: string, stageNote: string) => void;
}) {
  const [description, setDescription] = useState("Architectural design services");
  const [stageNote, setStageNote] = useState("");

  return (
    <FormDrawer
      title="New feedback request"
      onClose={onCancel}
      busy={busy}
      closeOnOverlayClick={false}
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button
            variant="primary"
            type="button"
            loading={busy}
            onClick={() => onConfirm(description.trim() || "Architectural design services", stageNote.trim())}
          >
            Send request
          </Button>
        </>
      }
    >
      <div className="feefo__confirm-body">
        <p className="job-drawer__muted" style={{ margin: 0 }}>
          Feefo will email an independent review invitation to this client. Check the details before sending — this is
          also the guard against emailing the wrong person.
        </p>
        <dl className="feefo__confirm">
          <div>
            <dt>Client</dt>
            <dd>{clientName || "—"}</dd>
          </div>
          <div>
            <dt>Email</dt>
            <dd>{clientEmail}</dd>
          </div>
          <div>
            <dt>Order reference</dt>
            <dd className="feefo__ref">{predictedOrderRef}</dd>
          </div>
        </dl>
        {recentlyAsked ? (
          <p className="feefo__warn">
            Last asked {lastAskedLabel} — send another? Re-asks are allowed (that's the point), but avoid over-emailing
            the same client.
          </p>
        ) : null}
        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          hint="Shown to Feefo as the service reviewed."
        />
        <TextAreaField
          label="Stage / reason (optional)"
          value={stageNote}
          onChange={(e) => setStageNote(e.target.value)}
          rows={2}
          hint="A private note for the history, e.g. “after permission granted”. Not sent to the client."
        />
      </div>
    </FormDrawer>
  );
}
